import BadgeIcon from '@mui/icons-material/Badge';
import GroupAddIcon from '@mui/icons-material/GroupAdd';

export const PAGE_IDS = {
	COMMUNITY_CONNECT_USER: 'communityconnectuser',
	EPICCARE_LINK_USER: 'epiccarelinkuser',
};

/**
 * Returns MUI Icons representing each UAR page
 */
export const PAGE_ICONS = {
	HOME: BadgeIcon,
	IDENTITY: GroupAddIcon,
};

/**
 * Returns a string to be used to create Block-Element-Modifier-style IDs for styles and clickable elements
 */
export const BEM_NAME_ELEMENTS = {
	VIEW_BREADCRUMBS: 'navigation-breadcrumbs',
	VIEW_FORM: 'form',
	VIEW_NAVIGATION: 'navigation',
	VIEW_EDIT: 'edit-profile',
	VIEW_DELEGATE: 'delegate',
	VIEW_REASSIGN: 'reassign',
	VIEW_RENEW: 'renew',
	VIEW_REVIEW: 'review',
	VIEW_SUMMARY: 'summary',
	VIEW_TERMINATE: 'terminate',
	FIELD_ACCORDION: 'accordion',
	FIELD_BUTTON: 'button',
	FIELD_CHECKBOX: 'checkbox',
	FIELD_LINK: 'link',
	FIELD_LIST: 'list',
	FIELD_OPTIONS: 'options',
	FIELD_RADIOS: 'radios',
	FIELD_SELECT: 'select',
	FIELD_SPEED_DIAL: 'speed-dial',
	FIELD_SWITCH: 'switch',
	FIELD_TEXT_FIELD: 'textfield',
	SUBMIT: 'submit',
};

/**
 * Returns the route path for each page
 */
export const PAGE_ROUTES = {
	LEGACY_HOME:
		'/index.html?page=%2FESO%2FNonEmployee_Agreement.aspx%3Fuser%3D0',
	HOME: '/NewNonEmployee/Home',
	COMMUNITY_CONNECT_USER: '/NewNonEmployee/CommunityConnectUser',
	EPICCARE_LINK_USER: '/NewNonEmployee/EpicCareLinkUser',
};

/**
 * Keys of the fields for the forms
 */
export const NEW_NON_EMPLOYEE_KEYS = {
	ACKNOWLEDGEMENT: 'acknowledgement',
	COST_CENTER: 'costCenter',
	DATE_OF_BIRTH: 'dob',
	DUPLICATE_ID_CHECK: 'duplicateIdCheck',
	ALTERNATE_EMAIL: 'email',
	FIRST_NAME: 'firstName',
	HOSPITAL_AFFILIATION: 'hospitalAffiliation',
	IDENTIFIER_NUMBER: 'identifierNumber',
	IDENTIFIER_TYPE: 'identifierType',
	IDENTIFIER_SPECIFIER: 'identifierSpecifier',
	IS_SITE_ADMIN: 'isSiteAdmin',
	JOB_TITLE: 'jobTitle',
	LAST_NAME: 'lastName',
	MIDDLE_INITIAL: 'middleInitial',
	NOT_A_DUPLICATE_ACKNOWLEDGEMENT: 'notADuplicateAcknowledgement',
	NOT_A_DUPLICATE_REASON: 'notADuplicateReason',
	PHONE_NUMBER: 'phoneNumber',
	PHONE_NUMBER_EXTENSION: 'phoneExtension',
	PRACTICE_ADDRESS_STREET1: 'practiceAddressStreet1',
	PRACTICE_ADDRESS_STREET2: 'practiceAddressStreet2',
	PRACTICE_ADDRESS_STREET3: 'practiceAddressStreet3',
	PRACTICE_ADDRESS_COUNTRY: 'practiceAddressCountry',
	PRACTICE_ADDRESS_STATE: 'practiceAddressState',
	PRACTICE_ADDRESS_ZIP: 'practiceAddressZip',
	PREREQUISITE_CHECK: 'prerequisiteCheck',
	REQUESTOR_IS_SITE_ADMIN: 'requestorIsSiteAdmin',
	SELF_SPONSOR: 'selfSponsor',
	SPONSOR: 'sponsor',
	THIRD_PARTY_ORGANIZATION: 'thirdPartyOrganization',
	WAVE_ONE_LOCATION: 'waveOneLocation',
};

/**
 * enumerate the identifier types
 * The integer values correspond to the primary keys from the T_IDENTIFIER_TYPE table
 */
export const IDENTIFIER_TYPES = {
	SOCIAL_SECURITY_NUMBER: 1,
	VISA: 2,
	PASSPORT: 3,
	DRIVERS_LICENSE: 4,
	EU_IDENTIFIER: 5,
	MICROSOFT_ID: 6,
	UNIQUE_SURVEYOR_ID: 7,
	UNIVERSITY_ID: 8,
	PARTNER_ID: 9,
	AADHAR_NUMBER: 11,
};

export const DUPLICATE_REASONS = {
	LOADING: -2,
	FORM_INCOMPLETE: -1,
	NONE: 0,
	IDENTITY_FOUND_NO_ESO: 1,
	ESO_FOUND: 2,
	ESO_FOUND_RETIRED: 3,
	ESO_FOUND_SAME_AS_REQUESTOR: 4,
	SUSPECTED_DUPLICATE_ESO: 5,
	UNKNOWN: 6,
	NONE_ATTACHED_IDENTITY: 7,
	INVALID_IDENTIFIER: 8,
};

export const NON_EMPLOYEE_USER_TYPES = [
	{
		id: 53,
		name: 'Administrative Assistant',
		description:
			'Administrative Assistant refers to people having no clinical role.',
		isLegacy: true,
	},
	{
		id: 52,
		name: 'Contract Personnel',
		description:
			'Contract Personnel refers to people working on a contract basis for a company that does business with UPMC.',
		isLegacy: true,
	},
	{
		id: 92,
		name: 'Community Connect User',
		description:
			'Personal information for Credentialed Faculty often preexists in the system. You can automatically import that information into the request form if you select SSN as the identifier on the next page.',
		isLegacy: false,
	},
	{
		id: 9,
		name: 'Credentialed Faculty',
		description:
			'Personal information for Credentialed Faculty often preexists in the system. You can automatically import that information into the request form if you select SSN as the identifier on the next page.',
		isLegacy: true,
	},
	{
		id: 18,
		name: 'Drug Company Clinical Trials Representative / Auditor',
		description:
			'User who monitors clinical drug trials being conducted at UPMC on behalf of the drug company that he or she represents.',
		isLegacy: true,
	},
	{
		id: 90,
		name: 'EpicCare Link User',
		description: '',
		isLegacy: false,
	},
	{
		id: 13,
		name: 'Healthcare Provider',
		description:
			'A Health Care Provider is an individual who uses or needs UPMC resources to provide health care services to patients.',
		isLegacy: true,
	},
	{
		id: 14,
		name: 'Regulatory User',
		description:
			'A regulatory user refers to a government surveyor or auditor who may require system access to perform official government business.',
		isLegacy: true,
	},
	{
		id: 15,
		name: 'Researcher',
		description:
			'A researcher is a user whose primary job function is research.',
		isLegacy: true,
	},
	{
		id: 23,
		name: 'Resident',
		description:
			'A resident is training under the supervision of an attending physician.',
		isLegacy: true,
	},
	{
		id: 10,
		name: 'Student',
		description:
			'A student of an academic program which provides practical workplace experience for academic credit under the terms set out in an agreement between UPMC and the educational institution. All students in a UPMC facility must complete the proper forms. Students are categorized under non-staff classifications.',
		isLegacy: true,
	},
	{
		id: 55,
		name: 'Tenant Badge Only',
		description:
			'Tenant-Badge Only account types are for users who need only a UPMC badge. If this user will need a UPMC network ID or access to any UPMC application, please choose a different account type.',
		isLegacy: true,
	},
	{
		id: 1,
		name: 'Vendor',
		description:
			'A Vendor refers to a company that does business with UPMC. Vendors typically provide goods and/or services to UPMC. Individuals who represent or work for a vendor are known as Vendor Staff.',
		isLegacy: true,
	},
	{
		id: 21,
		name: 'Vendor Support Account',
		description:
			"Vendor support accounts can be enabled or disabled as needed by the user's manager. This user type is denied access to clinical applications (i.e. Cerner, Epic, ARIA, etc.).",
		isLegacy: true,
	},
	{
		id: 22,
		name: 'Volunteer',
		description:
			'A person donating their time and talent in service at UPMC.',
		isLegacy: true,
	},
	{
		id: 12,
		name: 'Other',
		description:
			'A person that does not fit into any of the other categories, i.e., someone needing only badge access, visiting physician, summer intern, etc.',
		isLegacy: true,
	},
];
